import { useEffect, useMemo } from "react"
import { useStores } from "../../../models/helpers/useStores"
import { getRoomDetails } from "../BookingModal/useRoomDetails"
import { AnimatedTabs } from "../CruiseDetails/components/AnimatedTabs"
import styles from "./styles.module.scss"
import AlertCircleOutline from "~icons/eva/alert-circle-outline"
import { makeAutoObservable } from "mobx"
import { observer } from "mobx-react-lite"
import { FormSelectInput } from "../../atoms/FormSelectInput"
import FormTextInput from "../../atoms/FormTextInput"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { useMutation, useQuery } from "@tanstack/react-query"
import { sendPaymentRequest } from "../../../services/firebase-functions/billing"
import { CabinGradeType, ITripCollaborator } from "@cruisen/contract"
import { formatCurrency } from "@cruisen/i18n"
import classNames from "classnames"
import { getCruiseDetails } from "../../../services/firestore/cruiseDetails"
import { getSearchCriteriaHash } from "../../../models/helpers/getSearchCriteriaHash"
import { DAY_IN_MS, getAllCabinTypes, sortDecks } from "@cruisen/react-shared"
import { FormCurrencyInput } from "../../atoms/FormCurrencyInput"
import { colors } from "@theme/colors"
import { FormMultiSelectInput } from "../../atoms/FormMultiSelectInput"
import { getPaymentRequestList } from "../../molecules/Room/utils"
import { FormDatePicker } from "../../atoms/FormDatePicker"
import { currencyToNumber } from "../BookingModal/utils"
import { addMonths, format } from "date-fns"
import CloseModalButton from "../../buttons/CloseModalButton"
import { getMockedPaymentRequest } from "../../../utils/demoTripLogic"
import FormTextArea from "../../atoms/FormTextArea"

// Convert YYYY-MM-DD to MM/DD/YYYY (90 days from start date)
const getDefaultPaymentDate = (cruiseSailingDate: string): string | null => {
  if (!cruiseSailingDate) return null

  const isWithin90Days =
    new Date(cruiseSailingDate).getTime() < new Date().getTime() + 90 * DAY_IN_MS

  if (isWithin90Days) return null

  const dateParts = cruiseSailingDate.split("-")
  let year = parseInt(dateParts[0])
  let month = parseInt(dateParts[1])
  const day = parseInt(dateParts[2])

  if (month > 3) {
    month -= 3
  } else {
    month += 9
    year -= 1
  }

  return `${month < 10 ? `0${month}` : month}/${day < 10 ? `0${day}` : day}/${year}`
}

type PaymentRequestFormValues = {
  requestName: string
  assignees: { id: string; name: string }[]
  deck?: string
  cabinType: CabinGradeType
  cabinNo?: string
  taxesAndFees: string
  depositAmount: string
  paymentDate: string
  price: string
  termsAndConditions?: string
}

const paymentRequestSchema = yup.object().shape({
  requestName: yup.string().required("-"),
  assignees: yup.array().min(1, "-"),
  deck: yup.string().optional(),
  cabinType: yup.string().required("-"),
  cabinNo: yup.string().optional(),
  taxesAndFees: yup.string().required("-"),
  depositAmount: yup.string().required("-"),
  paymentDate: yup.string().required("-"),
  price: yup.string().required("-"),
  termsAndConditions: yup.string().optional(),
})

class PaymentRequestModalStore {
  currentRoomId: string

  constructor(initialRoomId: string) {
    this.currentRoomId = initialRoomId

    makeAutoObservable(this)
  }

  setCurrentRoomId(roomId: string) {
    this.currentRoomId = roomId
  }
}

export const PaymentRequestModal = observer(function PaymentRequestModal({
  closeModal,
  initialRoomId,
}: {
  closeModal: () => void
  initialRoomId: string
}) {
  const rootStore = useStores()
  const { activeTrip, loggedInUser } = rootStore
  const store = useMemo(() => new PaymentRequestModalStore(initialRoomId), [])
  const isDemoTrip = rootStore.activeTrip?.tags.includes("demo")

  const { tripRooms, roomData } = getRoomDetails(
    activeTrip.rooms,
    store.currentRoomId || initialRoomId,
  )

  const assigneeOptions = activeTrip.collaborators
    .filter(
      (collaborator: ITripCollaborator) =>
        collaborator.type !== "agent" && collaborator.userId !== loggedInUser.id,
    )
    .map((collaborator: ITripCollaborator) => ({
      label: `${collaborator.firstName} ${collaborator.lastName}`,
      value: collaborator.userId,
    }))
  const demoTripAssigneeOptions = [
    {
      label: "Demo User",
      value: "12345",
    },
  ]

  const cruiseDetailsQueryKey = [
    "cruiseDetails",
    rootStore.activeTrip.id,
    getSearchCriteriaHash(rootStore.activeTrip),
    rootStore.activeTrip.chosenTripSearchResultId,
  ]

  const { data: cruiseDetailsData, isLoading: isCruiseDetailsLoading } = useQuery({
    queryKey: cruiseDetailsQueryKey,
    queryFn: () =>
      getCruiseDetails(
        rootStore.activeTrip.id,
        rootStore.activeTrip.chosenTripSearchResultId,
        "main",
      ),
    enabled: !!activeTrip.chosenTripSearchResultId,
  })

  const decks = [...(cruiseDetailsData?.decks ?? [])]
  sortDecks(decks)

  const decksOptions = decks.map((deck) => ({ label: deck.name, value: String(deck.id) }))

  const cabinTypes = getAllCabinTypes()
  const cabinTypesOptions = cabinTypes.map((cabinType) => ({
    label: cabinType.label,
    value: cabinType.id,
  }))

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      requestName: "",
      assignees: [],
      deck: "",
      cabinType: "",
      cabinNo: "",
      taxesAndFees: "",
      depositAmount: "",
      paymentDate: "",
      price: "",
      termsAndConditions: "",
    },
    resolver: yupResolver(paymentRequestSchema),
    mode: "all",
  })

  useEffect(() => {
    if (isDemoTrip && !isCruiseDetailsLoading) {
      const demoAssignee = demoTripAssigneeOptions[0]

      setValue("requestName", "Demo Cabin Booking")
      setValue("assignees", [
        {
          id: demoAssignee.value,
          name: demoAssignee.label,
        },
      ])
      setValue("cabinType", cabinTypesOptions[0]?.value || "")
      setValue("deck", decksOptions[0]?.value || "")
      setValue("cabinNo", "A123")
      setValue("taxesAndFees", "199.99")
      setValue("depositAmount", "500.00")
      setValue("price", "1299.99")

      const futureDate = addMonths(new Date(), 1)
      setValue("paymentDate", format(futureDate, "MM/dd/yyyy"))

      trigger()
    }
  }, [isDemoTrip, isCruiseDetailsLoading])

  const [taxesAndFees, price, assignees, paymentDate] = watch([
    "taxesAndFees",
    "price",
    "assignees",
    "paymentDate",
  ])

  useEffect(() => {
    trigger()
  }, [paymentDate])

  const assigneesText = assignees.map((assignee) => assignee.name).join(", ")

  const { mutate: createPaymentRequest, isPending } = useMutation({
    mutationFn: async (data: PaymentRequestFormValues) => {
      const mockedPaymentRequest = getMockedPaymentRequest({
        tripId: activeTrip.id,
        roomId: store.currentRoomId,
        cruiseDetailsData,
      })

      const paymentRequestData = isDemoTrip
        ? mockedPaymentRequest
        : {
            paymentRequest: {
              tripId: activeTrip.id,
              label: data.requestName,
              termsAndConditions: data.termsAndConditions || "",
              assignedTo: data.assignees.map((assignee) => assignee.id),
              lineItems: [
                {
                  type: "cabinBooking" as const,
                  tripRoomId: store.currentRoomId,
                  currency: "USD",
                  amount: currencyToNumber(data.price).toString(),
                  taxesAndFees: currencyToNumber(data.taxesAndFees).toString(),
                  total: (
                    currencyToNumber(data.price) + currencyToNumber(data.taxesAndFees)
                  ).toString(),
                  totalDueDate: new Date(data.paymentDate),
                  deposit: currencyToNumber(data.depositAmount).toString(),
                  label: `Cabin ${data.cabinNo}`,
                  description: `Cabin ${data.cabinNo}, deck ${data.deck}, cruise ${cruiseDetailsData?.tripSearchResult?.name}`,
                  meta: {
                    cabinGradeName: cabinTypes.find((cabinType) => cabinType.id === data.cabinType)
                      ?.label,
                    cabinGradeType: data.cabinType,
                    cabinNo: data.cabinNo,
                    cruiseSailingId: cruiseDetailsData?.tripSearchResult.cruiseSailingId,
                    cruiseShipId: cruiseDetailsData?.ship.id,
                    deckId: Number(data.deck),
                    deckName: cruiseDetailsData?.decks.find((deck) => deck.id === Number(data.deck))
                      ?.name,
                  },
                },
              ],
            },
          }

      const paymentRequest = await sendPaymentRequest(paymentRequestData)
      return paymentRequest
    },
    onSuccess: () => {
      console.info("Payment request created")
      closeModal()
    },
  })

  const onSubmit = handleSubmit((data: PaymentRequestFormValues) => {
    createPaymentRequest(data)
  })

  const paymentRequestList = getPaymentRequestList(roomData)
  const hasActivePaymentRequest = paymentRequestList.some(
    (paymentRequest) =>
      paymentRequest.status !== "cancelledByAgent" && paymentRequest.status !== "rejectedByUser",
  )

  const defaultPaymentDate = getDefaultPaymentDate(cruiseDetailsData?.tripSearchResult.startDate)

  const isCruiseSailingDatePassed =
    cruiseDetailsData?.tripSearchResult?.startDate &&
    new Date(cruiseDetailsData?.tripSearchResult?.startDate).getTime() < new Date().getTime()

  return (
    <div className={styles.paymentRequestModal} data-testid="payment-request-modal">
      <div className={styles.header}>
        <div>
          <h2>Quote</h2>
          <AnimatedTabs
            chosenItem={roomData.id}
            items={tripRooms}
            onPress={(itemId) => {
              store.setCurrentRoomId(itemId)
            }}
          />
        </div>
        <CloseModalButton onClick={closeModal} testId="close-payment-request-modal-button" />
      </div>
      <div className={styles.content}>
        {isCruiseSailingDatePassed ? (
          <div className={classNames(styles.quoteWarning, styles.datePassedWarning)}>
            <AlertCircleOutline className={styles.quoteWarningIcon} />
            <p className={styles.quoteWarningText}>
              The cruise sailing date has passed. Please select a different cruise.
            </p>
          </div>
        ) : null}
        <div className={styles.paymentRequestSection}>
          <h3 className={styles.sectionTitle}>General information</h3>
          <div className={styles.inputRow}>
            {/* Temporarily only cabin booking is supported
            <FormSelectInput
              name="requestType"
              placeholder="Select request type"
              options={requestTypeOptions}
              control={control}
            /> */}
            <FormTextInput
              name="requestName"
              label="Quote name"
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testID="payment-request-name-input"
            />
            <FormMultiSelectInput
              name="assignees"
              label="Assign quote to"
              textValue={assigneesText}
              options={isDemoTrip ? demoTripAssigneeOptions : assigneeOptions}
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testID="assignees-select"
            />
          </div>
        </div>
        <div className={styles.paymentRequestSection}>
          <h3 className={styles.sectionTitle}>Cabin information</h3>
          <div className={styles.inputRow}>
            <FormSelectInput
              name="cabinType"
              label="Cabin type"
              options={cabinTypesOptions}
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testID="cabin-type-select"
            />
            <FormSelectInput
              name="deck"
              label="Deck (optional)"
              control={control}
              maxPopoverHeight={225}
              options={decksOptions}
              parentBackgroundColor={colors.backgroundSecondary}
              testID="deck-select"
            />
          </div>
          <div className={classNames(styles.inputRow, styles.singleInputRow)}>
            <FormTextInput
              name="cabinNo"
              label="Cabin number (optional)"
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testID="cabin-number-input"
            />
          </div>
        </div>
        <div className={styles.paymentRequestSection}>
          <h3 className={styles.sectionTitle}>Payment information</h3>
          <div className={styles.inputRow}>
            <FormCurrencyInput
              name="price"
              label="Cabin fare"
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testId="price-input"
            />

            <FormCurrencyInput
              name="taxesAndFees"
              label="Taxes and fees"
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testId="taxes-and-fees-input"
            />
          </div>
          <div className={styles.inputRow}>
            <FormCurrencyInput
              name="depositAmount"
              label="Deposit amount"
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testId="deposit-amount-input"
            />
            <div className={styles.paymentDateContainer}>
              <FormDatePicker
                inputName="paymentDate"
                label="Final due date"
                control={control}
                parentBackgroundColor={
                  !cruiseDetailsData ? colors.backgroundTertiary : colors.backgroundSecondary
                }
                onChange={(value) => setValue("paymentDate", value)}
                inOrder={true}
                enabledDateRange={{
                  minDate: new Date(),
                  maxDate: undefined,
                }}
                lightOutline={false}
                defaultDate={paymentDate}
                defaultOpenToDate={paymentDate || defaultPaymentDate}
                isDisabled={!cruiseDetailsData}
                testID="payment-date-picker"
              />
            </div>
          </div>
        </div>
        <div className={styles.paymentRequestSection}>
          <h3 className={styles.sectionTitle}>Terms & Conditions (optional)</h3>
          <div className={styles.inputRow}>
            <FormTextArea
              name="termsAndConditions"
              label="Add terms & conditions to this quote"
              control={control}
              parentBackgroundColor={colors.backgroundSecondary}
              testID="terms-and-conditions-input"
              rows={5}
              resize={false}
            />
          </div>
        </div>
        {hasActivePaymentRequest && !isPending ? (
          <div className={styles.quoteWarning} data-testid="payment-request-modal-quote-warning">
            <AlertCircleOutline className={styles.quoteWarningIcon} />
            <p className={styles.quoteWarningText}>
              Once you create new quote for this room, the previous one will be invalid.
            </p>
          </div>
        ) : null}
      </div>
      <div className={styles.footer}>
        <div className={styles.total}>
          <p className={styles.totalPrice}>
            Total:{" "}
            <span>
              {formatCurrency(currencyToNumber(price) + currencyToNumber(taxesAndFees), 2)}
            </span>
          </p>
          <p className={styles.taxesAndFeesIncluded}>taxes and fees included</p>
        </div>
        <button
          className={styles.submitButton}
          disabled={!isValid || isPending || isCruiseSailingDatePassed}
          onClick={onSubmit}
          data-testid="send-quote-button"
        >
          {isPending ? "Sending..." : "Send quote"}
        </button>
      </div>
    </div>
  )
})
